import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterPriceList, WaterPriceListCreateOrUpdate } from '@models/water-price-list';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWaterPriceListsService } from '@services/crm-water-price-lists.service';
import { ToastService } from '@services/toast.service';
import { firstValueFrom } from 'rxjs';
import { priceListErrorKey } from '../price-list-error';
import { WaterPriceListDialogComponent } from '../water-price-list-dialog/water-price-list-dialog.component';
import type { WaterPriceListDialogData } from '../water-price-list-dialog/water-price-list-dialog.model';

/**
 * "Add price list" (CS-232), wherever one is needed: the Price lists section, and beside each pricing
 * grid's price-list dropdown — including the empty state a lake with no price lists shows, which is
 * where it matters most. Owns the dialog, the POST and its toasts, and emits what was created, so the
 * create flow exists once rather than at each of those call sites.
 *
 * Where the caller will switch to the new price list — every grid does, since an empty grid is the
 * point of adding one — {@link hasUnsavedChanges} makes that switch consented to first, the same
 * guard the dropdown itself applies. Asking before the dialog opens rather than after keeps the
 * caller's {@link created} handler unconditional.
 */
@Component({
  selector: 'app-add-price-list-button',
  templateUrl: './add-price-list-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
})
export class AddPriceListButtonComponent {
  readonly waterId = input.required<number>();
  /** Whether adding would discard edits the caller has not saved, i.e. whether to confirm first. */
  readonly hasUnsavedChanges = input(false);
  readonly disabled = input(false);
  /** Render as an icon-only button, for sitting beside a form field rather than in a card header. */
  readonly compact = input(false);

  /** The price list that was created and stored. Never emits for a cancelled dialog or a failed POST. */
  readonly created = output<WaterPriceList>();

  private readonly priceListsService = inject(CrmWaterPriceListsService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  protected async add(): Promise<void> {
    if (this.hasUnsavedChanges()) {
      const confirmed = await this.confirmation.open({
        title: this.translate.instant('waters.price-lists.discard-title'),
        prompt: this.translate.instant('waters.price-lists.discard-prompt'),
      });
      if (!confirmed) return;
    }

    const data: WaterPriceListDialogData = { priceList: null };
    const dto = await firstValueFrom(
      this.dialog
        .open<WaterPriceListDialogComponent, WaterPriceListDialogData, WaterPriceListCreateOrUpdate>(
          WaterPriceListDialogComponent,
          { data, width: '480px' },
        )
        .afterClosed(),
    );
    if (dto == null) return;

    try {
      const priceList = await firstValueFrom(this.priceListsService.createPriceList(this.waterId(), dto));
      this.toastService.showSuccess(this.translate.instant('waters.price-lists.save-success'));
      this.created.emit(priceList);
    } catch (error: unknown) {
      this.toastService.showError(this.translate.instant(priceListErrorKey(error, 'waters.price-lists.save-failed')));
    }
  }
}
